/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  Box,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import { toast } from 'react-toastify';

import Modal from '../../componentes/Modal';
import endpoints, { PLANTILLAS_CONTRATO } from '../../configuraciones/endpoints';
import axios from '../../configuraciones/axios';
import { TIPOS_PLANTILLA_CONTRATO } from '../../constantes/variablesContrato';
import sanitizeContractHtml from '../../componentes/EditorContrato/utilidades/sanitizer';
import useStyles from './styles';

const ModalClonarPlantilla = ({
  open,
  onClose,
  tipoActual,
  plantillaActualId,
  onClonar,
}) => {
  const classes = useStyles();

  // Referencia para evitar condiciones de carrera en peticiones asíncronas
  const requestIdRef = useRef(0);

  // Estados de control para la secuencia de modales
  const [modalCambioTipoAbierto, setModalCambioTipoAbierto] = useState(false);
  const [modalSobreescribirAbierto, setModalSobreescribirAbierto] = useState(false);

  // Estados de datos de clonación
  const [tipoModal, setTipoModal] = useState('CREDITO');
  const [plantillasModal, setPlantillasModal] = useState([]);
  const [plantillaModalId, setPlantillaModalId] = useState('');
  const [cargandoPlantillasModal, setCargandoPlantillasModal] = useState(false);
  const [cargandoClonacion, setCargandoClonacion] = useState(false);

  const obtenerNombreTipo = (tipoKey) => {
    const item = TIPOS_PLANTILLA_CONTRATO.find((t) => t.id === tipoKey);
    return item ? item.label : tipoKey;
  };

  // Cargar las plantillas disponibles del tipo seleccionado
  const cargarPlantillasPorTipo = async (tipoAConsultar) => {
    if (!tipoAConsultar) {
      setPlantillasModal([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setCargandoPlantillasModal(true);
    try {
      const data = await axios.get(
        endpoints.base.opciones(PLANTILLAS_CONTRATO),
        { params: { tipo: tipoAConsultar } }
      );
      if (requestId === requestIdRef.current) {
        if (Array.isArray(data)) {
          // Filtrar por el tipo consultado y excluir la plantilla que se está editando actualmente
          const filtradas = data
            .filter((p) => p.tipo === tipoAConsultar)
            .filter((p) => String(p.id) !== String(plantillaActualId))
            .map(({ id, nombre, tipo }) => ({
              id,
              label: nombre,
              nombre,
              tipo,
            }));
          setPlantillasModal(filtradas);
        } else {
          setPlantillasModal([]);
        }
      }
    } catch {
      if (requestId === requestIdRef.current) {
        setPlantillasModal([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setCargandoPlantillasModal(false);
      }
    }
  };

  // Cuando se abre el modal principal, inicializar con el tipo actual del formulario
  useEffect(() => {
    if (open) {
      const tipoInicial = tipoActual || 'CREDITO';
      setTipoModal(tipoInicial);
      setPlantillaModalId('');
      cargarPlantillasPorTipo(tipoInicial);
      setModalCambioTipoAbierto(false);
      setModalSobreescribirAbierto(false);
    }
  }, [open, tipoActual, plantillaActualId]);

  // Al cambiar el tipo de plantilla en el dropdown del modal
  const handleCambioTipoModal = (nuevoTipo) => {
    setTipoModal(nuevoTipo);
    setPlantillaModalId('');
    cargarPlantillasPorTipo(nuevoTipo);
  };

  const plantillaSeleccionadaObj = plantillasModal.find(
    (p) => String(p.id) === String(plantillaModalId)
  );

  // Al presionar "Continuar" en el modal de selección
  const handleContinuarSeleccion = () => {
    if (!plantillaModalId) return;

    if (tipoModal !== tipoActual) {
      // Tipos diferentes: abrir primero el modal de notificación de cambio de tipo
      setModalCambioTipoAbierto(true);
    } else {
      // Mismo tipo: ir directo al modal de sobreescritura de contenido
      setModalSobreescribirAbierto(true);
    }
  };

  // Al aceptar el cambio de tipo en el modal intermedio
  const handleAceptarCambioTipo = () => {
    setModalCambioTipoAbierto(false);
    setModalSobreescribirAbierto(true);
  };

  // Al confirmar definitivamente la sobreescritura y clonación
  const handleConfirmarClonar = async () => {
    if (!plantillaModalId) return;
    try {
      setCargandoClonacion(true);
      const plantillaDb = await axios.get(
        endpoints.base.url(PLANTILLAS_CONTRATO, plantillaModalId)
      );

      if (plantillaDb?.contenido === undefined) {
        throw new Error('La plantilla seleccionada no cuenta con contenido.');
      }

      const nuevoContenido = sanitizeContractHtml(plantillaDb.contenido || '');
      const tipoFinal = plantillaDb?.tipo || plantillaSeleccionadaObj?.tipo || tipoModal;

      if (onClonar) {
        onClonar({
          contenido: nuevoContenido,
          tipo: tipoFinal,
        });
      }

      // Cerrar modales únicamente tras una clonación exitosa
      setModalSobreescribirAbierto(false);
      onClose();
    } catch (error) {
      const mensaje = error?.message && error.message !== 'Error'
        ? error.message
        : 'No fue posible clonar la plantilla. Intente nuevamente.';
      toast.error(mensaje);
    } finally {
      setCargandoClonacion(false);
    }
  };

  const handleCerrarTodo = () => {
    if (!cargandoClonacion) {
      setModalCambioTipoAbierto(false);
      setModalSobreescribirAbierto(false);
      onClose();
    }
  };

  return (
    <>
      {/* Modal 1: Selección del Tipo y Plantilla a Clonar */}
      <Modal
        titulo="Clonar desde otra plantilla"
        open={open && !modalCambioTipoAbierto && !modalSobreescribirAbierto}
        handleClose={handleCerrarTodo}
        showActions
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleCerrarTodo}
              className={classes.modalBtnCancelar}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleContinuarSeleccion}
              disabled={!plantillaModalId || cargandoPlantillasModal}
              className={classes.modalBtnContinuar}
            >
              Continuar
            </Button>
          </>
        }
      >
        <Box py={1}>
          <Typography className={classes.modalTextoPrincipal}>
            Seleccione el tipo de plantilla y la plantilla que desea clonar en el editor:
          </Typography>

          <FormControl fullWidth variant="outlined" className={classes.modalCampoContainer}>
            <Typography className={classes.modalCampoLabel}>
              Tipo de plantilla:
            </Typography>
            <Select
              value={tipoModal}
              onChange={(e) => handleCambioTipoModal(e.target.value)}
              className={classes.modalSelect}
            >
              {TIPOS_PLANTILLA_CONTRATO.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" className={classes.modalCampoContainer}>
            <Typography className={classes.modalCampoLabel}>
              Plantilla a clonar:
            </Typography>
            <Select
              value={plantillaModalId}
              onChange={(e) => setPlantillaModalId(e.target.value)}
              displayEmpty
              disabled={cargandoPlantillasModal || plantillasModal.length === 0}
              className={classes.modalSelect}
            >
              <MenuItem value="" disabled>
                {cargandoPlantillasModal
                  ? 'Cargando plantillas disponibles...'
                  : plantillasModal.length === 0
                  ? `No hay plantillas de tipo ${obtenerNombreTipo(tipoModal)} disponibles`
                  : 'Seleccione una plantilla'}
              </MenuItem>
              {plantillasModal.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Modal>

      {/* Modal 2: Advertencia de Tipo de Contrato Diferente */}
      <Modal
        titulo="Advertencia: Cambio de tipo de contrato"
        open={open && modalCambioTipoAbierto}
        handleClose={() => setModalCambioTipoAbierto(false)}
        showActions
        actions={
          <>
            <Button
              variant="outlined"
              onClick={() => setModalCambioTipoAbierto(false)}
              className={classes.modalBtnCancelar}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleAceptarCambioTipo}
              className={classes.modalBtnContinuar}
            >
              Continuar
            </Button>
          </>
        }
      >
        <Box className={classes.modalAdvertenciaContenido}>
          <Typography className={classes.modalAlertaBoxWarning}>
            ⚠️ <b>Atención:</b> El tipo de contrato de la plantilla seleccionada (<b>{obtenerNombreTipo(tipoModal)}</b>) no coincide con el tipo actualmente asignado en el formulario (<b>{obtenerNombreTipo(tipoActual)}</b>).
          </Typography>
          <Typography className={classes.modalTextoPrincipal}>
            Si continúa con la clonación, <b>el tipo de contrato del formulario se cambiará automáticamente a "{obtenerNombreTipo(tipoModal)}"</b>.
          </Typography>
          <Typography className={classes.modalTextoPrincipal}>
            ¿Desea continuar?
          </Typography>
        </Box>
      </Modal>

      {/* Modal 3: Advertencia de Sobreescritura de Contenido */}
      <Modal
        titulo="Advertencia: Sobreescribir contenido"
        open={open && modalSobreescribirAbierto}
        handleClose={handleCerrarTodo}
        showActions
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleCerrarTodo}
              disabled={cargandoClonacion}
              className={classes.modalBtnCancelar}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmarClonar}
              disabled={cargandoClonacion}
              className={classes.modalBtnConfirmar}
            >
              {cargandoClonacion ? 'Clonando...' : 'Sí, sobreescribir'}
            </Button>
          </>
        }
      >
        <Box className={classes.modalAdvertenciaContenido}>
          <Typography className={classes.modalTextoPrincipal}>
            Está a punto de clonar el machote de la plantilla:
          </Typography>
          <Typography className={classes.modalNombrePlantilla}>
            «{plantillaSeleccionadaObj?.label || 'Plantilla seleccionada'}» ({obtenerNombreTipo(tipoModal)})
          </Typography>
          <Typography className={classes.modalAlertaBox}>
            ⚠️ <b>Atención:</b> Todo el contenido, variables y formato redactados actualmente en el editor serán reemplazados por el contenido de esta plantilla. El formulario permanecerá abierto para que pueda revisar los datos y guardar posteriormente.
          </Typography>
          {tipoModal !== tipoActual && (
            <Typography style={{ fontSize: 13, color: '#4b5563', marginTop: 8 }}>
              * Se actualizará también el tipo de contrato del formulario a <b>{obtenerNombreTipo(tipoModal)}</b>.
            </Typography>
          )}
        </Box>
      </Modal>
    </>
  );
};

ModalClonarPlantilla.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tipoActual: PropTypes.string,
  plantillaActualId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClonar: PropTypes.func.isRequired,
};

ModalClonarPlantilla.defaultProps = {
  tipoActual: 'CREDITO',
  plantillaActualId: null,
};

export default React.memo(ModalClonarPlantilla);
